import type { LogLevel, LoggingConfig } from './config';
import { getEnvironmentConfig, shouldLog, createConfig } from './config';

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  module?: string;
  context?: Record<string, unknown>;
  data?: unknown;
  error?: Error;
  performance?: {
    duration?: number;
    operation?: string;
  };
}

interface LoggerOptions {
  module?: string;
  context?: Record<string, unknown>;
}

class Logger {
  private static instance: Logger | undefined;
  private config: LoggingConfig;
  private logs: LogEntry[] = [];
  private remoteQueue: LogEntry[] = [];
  private flushTimer?: ReturnType<typeof setTimeout>;

  private constructor(config?: Partial<LoggingConfig>) {
    this.config = config ? createConfig(config) : getEnvironmentConfig();
    this.initialize();
  }

  static getInstance(config?: Partial<LoggingConfig>): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger(config);
    }
    return Logger.instance;
  }

  /**
   * Create a child logger with specific module context
   */
  static createLogger(options: LoggerOptions): ModuleLogger {
    return new ModuleLogger(Logger.getInstance(), options);
  }

  /**
   * Update the logging configuration
   */
  updateConfig(config: Partial<LoggingConfig>) {
    this.config = createConfig(config);
    this.initialize();
  }

  /**
   * Get current configuration
   */
  getConfig(): LoggingConfig {
    return { ...this.config };
  }

  private initialize() {
    // Load persisted logs if enabled
    if (this.config.storage.persist) {
      this.loadPersistedLogs();
    }

    // Setup remote logging flush timer
    if (this.config.remote.enabled && this.config.remote.flushInterval) {
      if (this.flushTimer) {
        clearInterval(this.flushTimer);
      }
      this.flushTimer = setInterval(() => {
        this.flushRemoteLogs();
      }, this.config.remote.flushInterval);
    }
  }

  private loadPersistedLogs() {
    try {
      const stored = localStorage.getItem(this.config.storage.persistKey);
      if (stored) {
        const logs: unknown = JSON.parse(stored);
        if (Array.isArray(logs)) {
          this.logs = (logs as LogEntry[]).slice(-this.config.storage.maxEntries);
        }
      }
    } catch {
      // Error handled silently
    }
  }

  private persistLogs() {
    if (!this.config.storage.persist) {
      return;
    }

    try {
      localStorage.setItem(this.config.storage.persistKey, JSON.stringify(this.logs));
    } catch {
      // Error handled silently
    }
  }

  private shouldLog(level: LogLevel, module?: string): boolean {
    // Check module-specific log level
    if (module && this.config.filters.modules?.[module]) {
      return shouldLog(level, this.config.filters.modules[module]);
    }

    // Check global log level
    return shouldLog(level, this.config.level);
  }

  private formatMessage(entry: LogEntry): string {
    const parts: string[] = [];

    if (this.config.console.includeTimestamp) {
      parts.push(`[${entry.timestamp}]`);
    }

    if (this.config.console.includeLevel) {
      parts.push(`[${entry.level.toUpperCase()}]`);
    }

    if (entry.module) {
      parts.push(`[${entry.module}]`);
    }

    parts.push(entry.message);

    return parts.join(' ');
  }

  private formatData(data: unknown): unknown {
    if (!this.config.format.stringifyObjects) {
      return data;
    }

    try {
      return JSON.stringify(data, null, 2);
    } catch {
      return data;
    }
  }

  private log(
    level: LogLevel,
    message: string,
    options?: {
      module?: string;
      context?: Record<string, unknown>;
      data?: unknown;
      error?: Error;
      performance?: { duration?: number; operation?: string };
    },
  ) {
    // Check if we should log this
    if (!this.shouldLog(level, options?.module)) {
      return;
    }

    // Check filters
    if (this.config.filters.exclude?.some((pattern) => pattern.test(message))) {
      return;
    }

    if (
      this.config.filters.include?.length &&
      !this.config.filters.include.some((pattern) => pattern.test(message))
    ) {
      return;
    }

    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      module: options?.module,
      context: {
        ...this.config.defaultContext,
        ...options?.context,
      },
      data: options?.data,
      error: options?.error,
      performance: options?.performance,
    };

    // Add to in-memory logs
    if (this.config.storage.enabled) {
      this.logs.push(entry);
      if (this.logs.length > this.config.storage.maxEntries) {
        this.logs.shift();
      }
      this.persistLogs();
    }

    // Log to console
    if (this.config.console.enabled) {
      const consoleLevel = this.config.console.level || this.config.level;
      if (shouldLog(level, consoleLevel)) {
        this.logToConsole(entry);
      }
    }

    // Add to remote queue
    if (this.config.remote.enabled) {
      this.remoteQueue.push(entry);
      if (this.config.remote.batchSize && this.remoteQueue.length >= this.config.remote.batchSize) {
        this.flushRemoteLogs();
      }
    }

    // Check performance threshold
    if (
      this.config.performance.enabled &&
      options?.performance?.duration &&
      options.performance.duration > this.config.performance.slowThreshold
    ) {
      this.log('warn', `Slow operation detected: ${options.performance.operation}`, {
        ...options,
        data: {
          ...(typeof options.data === 'object' && options.data !== null ? options.data : {}),
          duration: options.performance.duration,
          threshold: this.config.performance.slowThreshold,
        },
      });
    }
  }

  private logToConsole(entry: LogEntry) {
    // eslint-disable-next-line no-console -- This is a logger, console usage is intentional
    const logFn = console[entry.level] as (...args: unknown[]) => void;
    const message = this.formatMessage(entry);
    const args: unknown[] = [message];

    if (entry.data !== undefined) {
      args.push(this.formatData(entry.data));
    }

    if (entry.error && this.config.format.includeStackTrace) {
      args.push('\nError:', entry.error.message);
      if (entry.error.stack) {
        args.push('\nStack:', entry.error.stack);
      }
    }

    if (entry.context && Object.keys(entry.context).length > 0) {
      args.push('\nContext:', entry.context);
    }

    logFn(...args);
  }

  private async flushRemoteLogs() {
    if (!this.config.remote.enabled || !this.config.remote.endpoint) {
      return;
    }

    if (this.remoteQueue.length === 0) {
      return;
    }

    const logsToSend = [...this.remoteQueue];
    this.remoteQueue = [];

    try {
      await fetch(this.config.remote.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          logs: logsToSend,
          context: this.config.defaultContext,
        }),
      });
    } catch {
      // Re-add logs to queue on failure
      this.remoteQueue.unshift(...logsToSend);
    }
  }

  // Public logging methods
  debug(message: string, data?: unknown) {
    this.log('debug', message, { data });
  }

  info(message: string, data?: unknown) {
    this.log('info', message, { data });
  }

  warn(message: string, data?: unknown) {
    this.log('warn', message, { data });
  }

  error(message: string, error?: Error | unknown, data?: unknown) {
    if (error instanceof Error) {
      this.log('error', message, { error, data });
    } else {
      this.log('error', message, { data: error || data });
    }
  }

  // Performance logging
  time(operation: string): () => void {
    const start = performance.now();
    return () => {
      const duration = performance.now() - start;
      this.log('debug', `Operation completed: ${operation}`, {
        performance: { duration, operation },
      });
    };
  }

  // Get logs
  getLogs(options?: {
    level?: LogLevel;
    module?: string;
    limit?: number;
    startTime?: Date;
    endTime?: Date;
  }): LogEntry[] {
    let logs = [...this.logs];

    if (options?.level) {
      logs = logs.filter((log) => log.level === options.level);
    }

    if (options?.module) {
      logs = logs.filter((log) => log.module === options.module);
    }

    if (options?.startTime) {
      const { startTime } = options; // Extract for type narrowing
      logs = logs.filter((log) => new Date(log.timestamp) >= startTime);
    }

    if (options?.endTime) {
      const { endTime } = options; // Extract for type narrowing
      logs = logs.filter((log) => new Date(log.timestamp) <= endTime);
    }

    if (options?.limit) {
      logs = logs.slice(-options.limit);
    }

    return logs;
  }

  clearLogs() {
    this.logs = [];
    this.remoteQueue = [];
    if (this.config.storage.persist) {
      localStorage.removeItem(this.config.storage.persistKey);
    }
  }

  // Export logs
  exportLogs(format: 'json' | 'csv' = 'json'): string {
    if (format === 'json') {
      return JSON.stringify(this.logs, null, 2);
    }

    // CSV format
    const headers = ['timestamp', 'level', 'module', 'message', 'data'];
    const rows = this.logs.map((log) => [
      log.timestamp,
      log.level,
      log.module || '',
      log.message,
      log.data ? JSON.stringify(log.data) : '',
    ]);

    return [headers.join(','), ...rows.map((row) => row.map((cell) => `"${cell}"`).join(','))].join(
      '\n',
    );
  }

  // Cleanup
  destroy() {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }
    this.flushRemoteLogs();
  }
}

/**
 * Module-specific logger that includes module context
 */
class ModuleLogger {
  constructor(
    private logger: Logger,
    private options: LoggerOptions,
  ) {}

  debug(message: string, data?: unknown) {
    this.logger.debug(message, {
      ...this.options,
      ...(typeof data === 'object' && data !== null ? data : { value: data }),
    });
  }

  info(message: string, data?: unknown) {
    this.logger.info(message, {
      ...this.options,
      ...(typeof data === 'object' && data !== null ? data : { value: data }),
    });
  }

  warn(message: string, data?: unknown) {
    this.logger.warn(message, {
      ...this.options,
      ...(typeof data === 'object' && data !== null ? data : { value: data }),
    });
  }

  error(message: string, error?: Error | unknown, data?: unknown) {
    if (error instanceof Error) {
      this.logger.error(message, error, {
        ...this.options,
        ...(typeof data === 'object' && data !== null ? data : { value: data }),
      });
    } else {
      this.logger.error(message, undefined, {
        ...this.options,
        ...(typeof error === 'object' && error !== null ? error : { value: error }),
        ...(typeof data === 'object' && data !== null ? data : { value: data }),
      });
    }
  }

  time(operation: string): () => void {
    const start = performance.now();
    return () => {
      const duration = performance.now() - start;
      this.logger.debug(`Operation completed: ${operation}`, {
        ...this.options,
        performance: { duration, operation },
      });
    };
  }
}

// Export singleton instance
export const logger = Logger.getInstance();

// Export factory for module-specific loggers
export const { createLogger } = Logger;
